import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Briefcase, ArrowLeft, Users, MessageSquare, BookOpen, Code, Rocket, FileText, Heart, Clipboard, Activity, Sun, Headphones, Send, Circle, Wifi, WifiOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
const ICON_MAP = { users: Users, headphones: Headphones, 'book-open': BookOpen, code: Code, rocket: Rocket, 'file-text': FileText, heart: Heart, clipboard: Clipboard, activity: Activity, sun: Sun };

export default function CommunityGroups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/groups`).then(r => { setGroups(r.data.groups); setLoading(false); }).catch(console.error);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectWS = useCallback((groupId) => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const wsUrl = `${WS_BASE}/api/ws/chat/${groupId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'system') {
        setMessages(prev => [...prev, { ...data, id: Date.now().toString() }]);
        if (data.online_count !== undefined) setOnlineCount(data.online_count);
        if (data.online_users) setOnlineUsers(data.online_users);
      } else if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      // Auto-reconnect after 3 seconds
      reconnectRef.current = setTimeout(() => {
        if (wsRef.current === ws) connectWS(groupId);
      }, 3000);
    };

    ws.onerror = () => ws.close();
    wsRef.current = ws;
  }, []);

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setMessages([]);
    setOnlineCount(0);
    setOnlineUsers([]);

    try {
      const { data } = await axios.get(`${API}/groups/${group.id}/messages?limit=50`);
      setMessages(data.messages || []);
      setOnlineCount(data.online_count || 0);
      setOnlineUsers(data.online_users || []);
    } catch (err) { console.error(err); }

    connectWS(group.id);
  };

  // Cleanup WS on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, []);

  const sendMessage = () => {
    if (!newMsg.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', content: newMsg }));
    setNewMsg('');
  };

  const joinGroup = async (groupId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/groups/${groupId}/join`, {}, { withCredentials: true });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, members: g.members + 1 } : g));
    } catch (err) { console.error(err); }
  };

  const formatTime = (isoString) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <h1 className="text-3xl font-medium tracking-tight mb-2" style={{fontFamily:'Outfit'}}>Community Groups</h1>
        <p className="text-[#1C2A24]/60 mb-8">Connect, chat, and network with fellow remote workers in real-time</p>

        <div className="grid md:grid-cols-3 gap-6" style={{height: 'calc(100vh - 240px)'}}>
          {/* Group List */}
          <div className="md:col-span-1 space-y-3 overflow-y-auto pr-2" data-testid="groups-list">
            {loading ? [1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse" />) :
              groups.map(group => {
                const IconComp = ICON_MAP[group.icon] || Users;
                const isActive = selectedGroup?.id === group.id;
                return (
                  <div key={group.id} onClick={() => selectGroup(group)}
                    className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all duration-300 ${isActive ? 'border-[#047857] shadow-[0_8px_20px_rgb(4,120,87,0.1)]' : 'border-[#E5E0D8] hover:border-[#047857]/30'}`}
                    data-testid={`group-${group.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-[#047857]' : 'bg-[#A7F3D0]/30'}`}>
                        <IconComp size={18} className={isActive ? 'text-white' : 'text-[#047857]'} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{group.name}</h3>
                        <p className="text-xs text-[#1C2A24]/50">{group.members.toLocaleString()} members</p>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>

          {/* Chat Area */}
          <div className="md:col-span-2 flex flex-col">
            {!selectedGroup ? (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center flex-1 flex flex-col items-center justify-center">
                <MessageSquare size={48} className="text-[#E5E0D8] mb-4" strokeWidth={1.5} />
                <p className="text-[#1C2A24]/60 text-sm">Select a group to start chatting</p>
                <p className="text-[#1C2A24]/40 text-xs mt-1">Join real-time conversations with WFH workers</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col flex-1 overflow-hidden" data-testid="group-chat">
                {/* Chat Header */}
                <div className="p-5 border-b border-[#E5E0D8] bg-[#F4F1E8]/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>{selectedGroup.name}</h2>
                      <p className="text-xs text-[#1C2A24]/50 mt-0.5">{selectedGroup.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 border border-[#E5E0D8]" data-testid="online-indicator">
                        {connected ? (
                          <Wifi size={12} className="text-[#047857]" strokeWidth={2} />
                        ) : (
                          <WifiOff size={12} className="text-[#D97757]" strokeWidth={2} />
                        )}
                        <span className="text-xs font-medium" style={{fontFamily:'Outfit'}}>{onlineCount} online</span>
                      </div>
                      <Button onClick={() => joinGroup(selectedGroup.id)} size="sm" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-xs h-8 px-4" data-testid="join-group-btn">Join</Button>
                    </div>
                  </div>
                  {/* Online Users */}
                  {onlineUsers.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap" data-testid="online-users">
                      {onlineUsers.slice(0, 8).map((name, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-[#A7F3D0]/20 text-[#047857] px-2 py-0.5 rounded-full">
                          <Circle size={5} className="fill-[#047857] text-[#047857]" />{name}
                        </span>
                      ))}
                      {onlineUsers.length > 8 && <span className="text-[10px] text-[#1C2A24]/40">+{onlineUsers.length - 8} more</span>}
                    </div>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chat-messages" style={{minHeight: '300px', maxHeight: 'calc(100vh - 440px)'}}>
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <MessageSquare size={32} className="text-[#E5E0D8] mb-3" strokeWidth={1.5} />
                      <p className="text-sm text-[#1C2A24]/40">No messages yet. Say hello!</p>
                    </div>
                  ) : (
                    messages.map(msg => (
                      msg.type === 'system' ? (
                        <div key={msg.id} className="text-center" data-testid="system-message">
                          <span className="text-[10px] text-[#1C2A24]/30 bg-[#F4F1E8]/50 px-3 py-1 rounded-full">{msg.content}</span>
                        </div>
                      ) : (
                        <div key={msg.id} className={`flex gap-3 ${msg.user_id === user?._id ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.id}`}>
                          <div className="w-8 h-8 rounded-full bg-[#047857]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-medium text-[#047857]">{(msg.user_name || 'A')[0].toUpperCase()}</span>
                          </div>
                          <div className={`max-w-[70%] ${msg.user_id === user?._id ? 'items-end' : ''}`}>
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-medium text-[#1C2A24]/70">{msg.user_name}</span>
                              <span className="text-[10px] text-[#1C2A24]/30">{formatTime(msg.created_at)}</span>
                            </div>
                            <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.user_id === user?._id ? 'bg-[#047857] text-white rounded-tr-sm' : 'bg-[#F4F1E8] text-[#1C2A24]/80 rounded-tl-sm'}`}>
                              {msg.content}
                            </div>
                          </div>
                        </div>
                      )
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Message Input */}
                {user ? (
                  <div className="p-4 border-t border-[#E5E0D8] bg-white" data-testid="chat-input-section">
                    <div className="flex gap-3 items-center">
                      <Input
                        value={newMsg}
                        onChange={e => setNewMsg(e.target.value)}
                        placeholder={connected ? "Type a message..." : "Reconnecting..."}
                        disabled={!connected}
                        className="rounded-full bg-[#F4F1E8] border-[#E5E0D8] h-11"
                        data-testid="chat-input"
                        onKeyDown={e => e.key === 'Enter' && sendMessage()}
                      />
                      <Button onClick={sendMessage} disabled={!connected || !newMsg.trim()} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11 w-11 shrink-0 p-0" data-testid="send-btn">
                        <Send size={16} strokeWidth={1.5} />
                      </Button>
                    </div>
                    {!connected && (
                      <p className="text-[10px] text-[#D97757] mt-2 flex items-center gap-1">
                        <WifiOff size={10} strokeWidth={1.5} /> Reconnecting to chat...
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border-t border-[#E5E0D8] bg-[#F4F1E8]/50 text-center">
                    <p className="text-sm text-[#1C2A24]/60">
                      <Link to="/auth" className="text-[#047857] font-medium hover:underline">Sign in</Link> to join the conversation
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
